/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  CommonActions,
  ComponentItemFinderDialogConfig,
  componentItemFinderDialogRoute,
  ComponentItemFinderListItem,
  CostCenterFinderDialogConfig,
  costCenterFinderDialogRoute,
  CostCenterFinderListItem,
  FormActionPipelines,
  FormFlowState,
  FormulaCalculationService,
  FormulaPropagationService,
  FormulaTestEvent,
  getComponentItemFinderListItemId,
  GraphicalTakeOffMainConfig,
  graphicalTakeOffMainDialogRoute,
  isFormulaVariableActive,
  UsageDialogConfig,
  usageDialogRoute,
  variablesToSnippets,
} from '@gh/builder-shared';
import {
  ComponentItemFilterMode,
  Formula,
  FormulaElementGroup,
  HpeInitType,
  UsageEntityType,
} from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  ContainerState,
  getAllNewSelectedEntities,
  getRouteInitData,
  getRouteParam,
  getSingleNewSelectedEntity,
  NumberTypes,
  PAGE_STATE,
  queryDirty,
  queryFormMode,
  queryFormModeCreate,
  State,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { MessageBasket, MessageSeverity } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { concat$ as concatFrom$, EMPTY$, of$, throwError$, combineLatest$ } from '@gh/rx';
import { concatWith$, map$, finalize$, filter$, first$, switchAll$, switchMap$, tap$ } from '@gh/rx/operators';

import { flatMap, isNil, keyBy, uniq } from 'lodash';
import { Observable } from 'rxjs';
import { isFormulaEmpty, isFormulaValid } from '@gh/builder-shared-data';

import { HousePlanEstimateDetailsBusinessService } from '../../resources/house-plan-estimate-details-business.service';
import {
  getHpeComponentId,
  getHpeCostCenterId,
  getHpeItemId,
  getHpeTopId,
  HpeComponent,
  HpeCostCenter,
  hpeCostCenterFromFinder,
  hpeCostCenterItemsFromFinder,
  HpeDetails,
  HpeDetailsGetCostCenter,
  HpeElement,
  HpeElementType,
  hpeFormulaVariableFromTop,
  HpeItem,
  HpeMappingContext,
  HpeTop,
  HpeTopViewUsageRecord,
  isHpeCcNotPreloaded,
  isHpeComponent,
  isHpeRecordForComponent,
  newHpeCuttingListItem,
} from '../../resources/hpe-details.model';
import {
  HpeDetailsState,
  HpeDetailsStateCostCenterLoadEvent,
  HpeDetailsStateEvent,
  HpeDetailsStateEventType,
  queryHpeComponent,
  queryHpeCostCenter,
  queryHpeCostCenterHasDirtyChanges,
  queryHpeCostCenters,
  queryHpeElement,
  queryHpeElementFormulaCalculation,
  queryHpeElementHasErrors,
  queryHpeFormulaFirstInvalidTop,
  queryHpeGetCostCenterRequest,
  queryHpeHasErrors,
  queryHpeHasTopsFromGto,
  queryHpeHousePlanId, queryHpeIsCccId,
  queryHpeItem,
  queryHpeMasterInfo,
  queryHpePriceBookId,
  queryHpeSaveModel,
  queryHpeSelectedCostCenterId,
  queryHpeSelectedElementInfo,
  queryHpeTop,
  queryHpeTopRefItem,
  queryHpeTopRefItems,
  queryHpeTops,
  queryHpeTopViewUsage,
  queryHpeUomRefItem,
  queryHpeUndoDisabled,
  queryHpeRedoDisabled,
  queryIsCccSelected,
  queryHpeFormulaHasTopsFromGto,
} from './hpe-details.state';
import { hpeOrderDetailsDialogRoute } from './hpe-order-details-dialog/hpe-order-details-dialog.component';
import { hpeTopViewUsageDialogRoute } from './hpe-top-usage-dialog/hpe-top-usage-dialog.component';

@Injectable()
export class HpeCommandService {
  isUndoDisabled$ = this.state.select(queryHpeUndoDisabled);
  isRedoDisabled$ = this.state.select(queryHpeRedoDisabled);
  memoDebounce: ReturnType<typeof setTimeout>;

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              @Inject(PAGE_STATE) private form: ContainerState<HpeDetailsState>,
              private state: State<HpeDetailsState>,
              private business: HousePlanEstimateDetailsBusinessService,
              private waitingService: WaitingService,
              private dialogService: DialogService,
              private notificationService: NotificationService,
              private formulaPropagation: FormulaPropagationService,
              private formulaCalculation: FormulaCalculationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private router: AppRouter) {

  }

  get housePlanId(): ID {
    return getRouteParam(this.route, 'housePlanId');
  }

  initHistory() {
    this.state.mutate({ type: HpeDetailsStateEventType.InitHistory});
  }
  saveViewToHistory() {
    this.state.mutate({ type: HpeDetailsStateEventType.SaveViewToHistory});
  }
  addToHistory() {
    this.state.mutate({ type: HpeDetailsStateEventType.AddToHistory});
  }
  undo() {
    this.state.mutate({ type: HpeDetailsStateEventType.Undo});
  }
  redo() {
    this.state.mutate({ type: HpeDetailsStateEventType.Redo});
  }

  load(details: HpeDetails): void {
    this.state.mutate({ type: HpeDetailsStateEventType.Load, details });
  }

  costCenterAdd(): void {
    this.dialogService.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
      multiselection: false,
      preselected: this.state.project(queryHpeCostCenters).map(getHpeCostCenterId),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((costCenter: CostCenterFinderListItem) => {
        this.state.mutate({
          type: HpeDetailsStateEventType.CostCenterAdd,
          costCenter: hpeCostCenterFromFinder(costCenter),
        });
        this.addToHistory();
      }
    );
  }

  costCenterViewOrderDetails(costCenter: HpeCostCenter): void {
    const masterInfo = this.state.project(queryHpeMasterInfo);
    this.dialogService.openWithInput(hpeOrderDetailsDialogRoute, {
      ccRequest: this.state.project(queryHpeGetCostCenterRequest, getHpeCostCenterId(costCenter)),
      hpName: masterInfo.name,
      hpCode: masterInfo.code,
    });
  }

  costCenterSetField(costCenterId: ID, name: string, value: any): void {
    const doAction = () => {
      this.saveViewToHistory();
      this.state.mutate({ type: HpeDetailsStateEventType.CostCenterSetField, costCenterId, name, value });
      this.addToHistory();
    }
    if (this.memoDebounce) clearTimeout(this.memoDebounce);
    if (name === 'memo') {
      this.memoDebounce = setTimeout(() => doAction(), 500);
    } else doAction();
  }

  costCenterRemove(costCenter: HpeCostCenter): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_he_cc_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.state.mutate({
          type: HpeDetailsStateEventType.CostCenterRemove,
          costCenterId: getHpeCostCenterId(costCenter),
        });
        this.addToHistory();
      });
  }

  costCenterSelect(costCenterId?: ID): void {
    if (!this.validate()) {
      return;
    }

    concatFrom$(
      this.propagateFormulaFlow(),
      this.ensureCcLoad(costCenterId),
      of$({ type: HpeDetailsStateEventType.CostCenterSelect, costCenterId }),
    ).subscribe((event) => this.state.mutate(event));
  }

  topViewInfo(top: HpeTop): void {
    const viewUsage = this.state.project(queryHpeTopViewUsage, getHpeTopId(top));
    this.dialogService.openWithInput(hpeTopViewUsageDialogRoute, viewUsage)
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((record: HpeTopViewUsageRecord) => {
        const currentCcId = this.state.project(queryHpeSelectedCostCenterId);

        if (isHpeRecordForComponent(record)) {
          if (!this.state.project(queryHpeIsCccId, currentCcId) && !this.validate()) {
            return;
          }

          this.componentSelect(record.recordId);
        } else {
          const item = this.state.project(queryHpeItem, record.recordId);
          if (item && item.costCenterId !== currentCcId && !this.validate()) {
            return;
          }
          this.itemSelect(record.recordId);
        }
      });
  }

  topRemove(top: HpeTop): void {
    this.state.mutate({ type: HpeDetailsStateEventType.TopRemove, topId: top.topId });
    this.addToHistory();
  }

  topSetField(topId: ID, name: string, value: any): void {
    const top = this.state.project(queryHpeTop, topId);

    if (top && top[name] !== value) {
      // set top value temporarily
      this.state.mutate({ type: HpeDetailsStateEventType.TopSetField, topId, name, value });

      if (top.isFromGto) {
        this.dialogService.confirmation(yesNoConfirmation('msg_top_gto_remove'))
          .subscribe((answer) => {
            // commit change if user has confirmed change, otherwise rollback original value
            if (yesAnswer(answer)) {
              this.state.mutate({ type: HpeDetailsStateEventType.TopCommit, topId });
            } else {
              this.state.mutate({ type: HpeDetailsStateEventType.TopReset, top });
            }
          });
      } else {
        this.state.mutate({ type: HpeDetailsStateEventType.TopCommit, topId });
      }

      this.addToHistory();
    }
  }

  componentAdd(): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      filterMode: ComponentItemFilterMode.Components,
      disableFilterMode: true,
      multiselection: true,
      isNumericSelector: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities),
      switchMap$((items: ComponentItemFinderListItem[]) => this.ensureDependenciesExist(items).pipe(map$(() => items))),
      switchMap$((components) =>
        this.business.getComponents(
          this.housePlanId,
          components.map(getComponentItemFinderListItemId),
          this.createHpeMappingContext()).pipe(
          this.waitingService.operator())))
      .subscribe((componentSet) => {
        this.state.mutate({ type: HpeDetailsStateEventType.ComponentAddSet, componentSet });
        this.addToHistory();
      }
    );
  }

  hpeComponentClone(component: HpeComponent): void {
    const componentToClone = component.componentId;
    if (componentToClone) {
      this.business.getComponents(
        this.housePlanId,
        [{ id: componentToClone, count: 1 }],
        this.createHpeMappingContext()
      ).pipe(
        map$((componentSet) => ({
          ...componentSet,
          components: componentSet.components.map(c => ({
            ...c,
            quantity: component.quantity,
            extension: component.extension,
            formula: component.formula,
            memo: component.memo,
            cuttingList: component.cuttingList,
          }))
        })),
        this.waitingService.operator()
      ).subscribe((componentSet) => {
        this.state.mutate({ type: HpeDetailsStateEventType.ComponentAddSet, componentSet });
        this.addToHistory();
      });
    }
  }

  componentSetField(componentId: ID, name: string, value: any): void {
    const doAction = () => {
      this.saveViewToHistory();
      this.state.mutate({ type: HpeDetailsStateEventType.ComponentSetField, componentId, name, value });
      this.addToHistory();
    }
    if (this.memoDebounce) clearTimeout(this.memoDebounce);
    if (name === 'memo') {
      this.memoDebounce = setTimeout(() => doAction(), 500);
    } else doAction();
  }

  componentRemove(component: HpeComponent): Observable<void> {
    return this.dialogService
      .confirmation(yesNoConfirmation('msg_he_component_delete')).pipe(
        filter$(yesAnswer),
        tap$(() => {
          this.state.mutate({
            type: HpeDetailsStateEventType.ComponentRemove,
            componentId: getHpeComponentId(component),
          });
          this.addToHistory();
        }),
        map$(() => void 0),
      );
  }

  removeComponents(componentIds: ID[]): void {
    if (!componentIds || componentIds.length === 0) {
      return;
    }

    componentIds.forEach((componentId) => {
      this.state.mutate({
        type: HpeDetailsStateEventType.ComponentRemove,
        componentId,
      });
    });
    this.addToHistory();
  }

  componentSelect(componentId?: ID): void {
    if (!this.validateCurrentElement()) {
      return;
    }

    concatFrom$(
      this.propagateFormulaFlow(),
      of$({ type: HpeDetailsStateEventType.ComponentSelect, componentId }),
    ).subscribe((event) => this.state.mutate(event));
  }

  cuttingListItemAdd(sourceType: HpeElementType, sourceId: ID): void {
    const parent = this.state.project(queryHpeElement, sourceType, sourceId);

    this.state.mutate({
      type: HpeDetailsStateEventType.CuttingListItemAdd,
      sourceType,
      sourceId,
      item: newHpeCuttingListItem(parent.uomId, -1, sourceType, sourceId, this.createHpeMappingContext()),
    });

    this.addToHistory();
  }

  cuttingListItemSetField(sourceType: HpeElementType,
                          sourceId: ID,
                          cuttingId: ID,
                          name: string,
                          value: any): void {
    this.state.mutate({
      type: HpeDetailsStateEventType.CuttingListItemSetField,
      sourceType,
      sourceId,
      cuttingId,
      name,
      value,
    });
    this.addToHistory();
  }

  cuttingListItemRemove(sourceType: HpeElementType, sourceId: ID, cuttingId: ID): void {
    this.state.mutate({
      type: HpeDetailsStateEventType.CuttingListItemRemove,
      sourceType,
      sourceId,
      cuttingId,
    });
    this.addToHistory();
  }

  costCenterItemAdd(costCenterId: ID): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      filterMode: ComponentItemFilterMode.Items,
      disableFilterMode: true,
      multiselection: true,
      isNumericSelector: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities),
      switchMap$((items: ComponentItemFinderListItem[]) => this.ensureDependenciesExist(items).pipe(map$(() => items))),
      switchMap$((items) => {
        const duplicatedItems: ComponentItemFinderListItem[] = items.reduce((acc, item) => {
          const count = item.count || 1;
          const copies = Array(count).fill(null).map(() => ({ ...item }));
          return acc.concat(copies);
        }, [] as ComponentItemFinderListItem[]);
        
        return this.business.generateItemIds(this.housePlanId, duplicatedItems.length).pipe(
          map$((ids) => hpeCostCenterItemsFromFinder(
            costCenterId,
            ids,
            duplicatedItems,
            this.createHpeMappingContext())),
          this.waitingService.operator())
      }))
      .subscribe((items: HpeItem[]) => {
        this.state.mutate({
          type: HpeDetailsStateEventType.CostCenterItemAddSet,
          costCenterId,
          itemSet: items,
        });
        this.addToHistory();
      });
  }

  costCenterItemClone(item: HpeItem): void {
    if (item) {
      this.business.generateItemIds(this.housePlanId, 1).pipe(
        map$((ids) => ([{
          
          ...item,
          hpItemId: ids[0],
          quantity: item.quantity,
          extension: item.extension,
          formula: item.formula,
          memo: item.memo,
          cuttingList: item.cuttingList,
          createdDate: new Date()   
        }])),
        this.waitingService.operator()
      ).subscribe((items: HpeItem[]) => {
        this.state.mutate({
          type: HpeDetailsStateEventType.CostCenterItemAddSet,
          costCenterId: item.costCenterId,
          itemSet: items,
        });
        this.addToHistory();
      });
    }
  }

  costCenterItemSetField(itemId: ID, name: string, value: any): void {
    const doAction = () => {
      this.saveViewToHistory();
      this.state.mutate({ type: HpeDetailsStateEventType.CostCenterItemSetField, itemId, name, value });
      this.addToHistory();
    }
    if (this.memoDebounce) clearTimeout(this.memoDebounce);
    if (name === 'memo') {
      this.memoDebounce = setTimeout(() => doAction(), 500);
    } else doAction();
  }

  costCenterItemRemove(item: HpeItem): Observable<void> {
    return this.dialogService
      .confirmation(yesNoConfirmation('msg_he_item_delete')).pipe(
        filter$(yesAnswer),
        tap$(() => {
          this.state.mutate({
            type: HpeDetailsStateEventType.CostCenterItemRemove,
            itemId: getHpeItemId(item),
          });
          this.addToHistory();
        }),
        map$(() => void 0),
      );
  }

  removeItems(itemIds: ID[]): void {
    if (!itemIds || itemIds.length === 0) {
      return;
    }

    itemIds.forEach((itemId) => {
      this.state.mutate({
        type: HpeDetailsStateEventType.CostCenterItemRemove,
        itemId,
      });
    });
    this.addToHistory();
  }

  itemSelect(itemId?: ID): void {
    if (!this.validateCurrentElement()) {
      return;
    }

    concatFrom$(
      this.propagateFormulaFlow(),
      of$(itemId).pipe(
        filter$(Boolean),
        map$((id) => this.state.project(queryHpeItem, id)),
        filter$(Boolean),
        switchMap$((item: any) => this.ensureCcLoad(item.costCenterId))),
      of$({ type: HpeDetailsStateEventType.CostCenterItemSelect, itemId }),
    ).subscribe((event) => this.state.mutate(event));
  }

  /**
   * We have separate method for cost center item selection accepting additional costCenter parameter.
   * This is necessary at least for component item selection, because target item is not stored in the store,
   * thus it is impossible to determine target cost center.
   *
   * @param {ID} costCenterId
   * @param {ID} itemId
   */
  costCenterItemSelect(costCenterId: ID, itemId: ID): void {
    if (!this.validateCurrentElement()) {
      return;
    }

    concatFrom$(
      this.ensureCcLoad(costCenterId),
      of$({ type: HpeDetailsStateEventType.CostCenterItemSelect, itemId }),
    ).subscribe((event) => this.state.mutate(event));
  }

  tabSelect(name: string): void {
    if (!this.validateCurrentElement()) {
      return;
    }

    this.state.mutate({ type: HpeDetailsStateEventType.DetailsTabSelect, name });
  }

  autoSave(): Observable<any> {
    if (!this.state.project(queryDirty)) {
      return EMPTY$;
    }
    const savingNotification = this.notificationService.show({
      messageId: 'lbl_saving',
      severity: MessageSeverity.Success,
      hideDelay: 30000,
    });

    const flow = this.completeFlow();
    return flow.run({ trigger: 'process', value: this.form.submitValue, form: this.form }).toFinalState().pipe(
      finalize$(() => {
        savingNotification.close();
      })
    );
  }

  recalculate(): void {
    if (!this.validate()) {
      return;
    }
    let recalculateFlowRun = false;
    concatFrom$(
      this.propagateFormulaFlow(),
      of$(null)
    ).subscribe((event) => {
      if (event) {
        this.state.mutate(event);
      }
      if (!recalculateFlowRun) {
        recalculateFlowRun = true;
        const flow = this.recalculateFlow();
        flow.run({
          form: this.form,
          value: this.form.submitValue,
        });
      }
    });
  }

  completeFlow(): ActionFlow<FormFlowState<any>> {
    const { state } = this;

    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => this.state.project(queryHpeHasErrors),
        () => this.handleFormError()),
      execute: this.commonActions.execute((fs: FormFlowState<any>) =>
        this.business.process(
          state.project(queryHpeSaveModel),
          state.project(queryIsCccSelected) ? void 0 : state.project(queryHpeSelectedCostCenterId),
          this.createHpeMappingContext())),
      finish: (fs: FormFlowState<any>) => {
        const isCreateMode = this.state.project(queryFormModeCreate);

        this.state.mutate({
          type: HpeDetailsStateEventType.LoadWithCostCenter,
          details: fs.value,
          costCenterId: fs.value.initialCostCenterId,
          costCenter: fs.value.initialCostCenter,
        });

        this.initHistory();

        if (isCreateMode) {
          // navigate to open mode after creation
          this.router.switchTo(['/estimating/house-plans', this.state.project(queryHpeHousePlanId), 'estimate'], {
            queryParams: { initType: HpeInitType.Open },
          });
        } else {
          this.updateFormulaContext();
        }
      },
    });
  }

  process(): void {
    if (!this.validate()) {
      return;
    }
    let completeFlowRun = false;
    concatFrom$(
      this.propagateFormulaFlow(),
      of$(null)
    ).subscribe((event) => {
      if (event) {
        this.state.mutate(event);
      }
      if (!completeFlowRun) {
        completeFlowRun = true;
        const flow = this.completeFlow();
        flow.run({
          trigger: 'process',
          form: this.form,
          value: this.form.submitValue,
        });
      }
    });
  }

  recalculateFlow(): ActionFlow<FormFlowState<HpeDetailsState>> {
    return this.pipelines.recalculate({
      validateForm: this.commonActions.validateWithEffect(
        () => this.state.project(queryHpeHasErrors),
        () => this.handleFormError()),
      execute: this.commonActions.execute((state: FormFlowState<HpeDetailsState>) =>
        <any>this.business.recalculate(this.state.project(queryHpeSaveModel), this.createHpeMappingContext())),
      finish: (state: FormFlowState<HpeDetailsState>) => {
        this.state.mutate({
          type: HpeDetailsStateEventType.Merge,
          details: state.value,
        });

        this.updateFormulaContext();
      },
    });
  }

  handleFormError(): void {
    if (!this.validateCurrentElement()) {
      return;
    }

    this.state.mutate({ type: HpeDetailsStateEventType.NavigateToInvalid });

    this.notificationService.error('msg_he_incorrect_data_in_tables');
  }

  resetFocusedCell(): void {
    this.state.mutate({ type: HpeDetailsStateEventType.ResetFocusedCell });
  }

  propagateItemFormula(item: HpeItem): void {
    this.propagateFormulaItemFlow(item);
  }

  propagateComponentFormula(component: HpeComponent): void {
    this.propagateFormulaComponentFlow(component);
  }

  testItemFormula(event: FormulaTestEvent, itemId: ID): void {
    const item = this.state.project(queryHpeItem, itemId);
    if (item) {
      this.testElementFormula(event, item);
    }
  }

  testComponentFormula(event: FormulaTestEvent, componentId: ID): void {
    const component = this.state.project(queryHpeComponent, componentId);
    if (component) {
      this.testElementFormula(event, component);
    }
  }

  openUsedBy(): void {
    this.dialogService.openWithInput(usageDialogRoute, <UsageDialogConfig>{
      entityId: this.state.project(queryHpeHousePlanId),
      entityType: UsageEntityType.HOUSE_PLAN_USAGE_INFO,
    });
  }

  openGto(): void {
    const mode = this.state.project(queryFormMode);
    switch (mode) {
      case FormMode.CreateMode:
        // GTO cannot be opened by Create mode
        this.notificationService.error('msg_save_first');
        break;
      case FormMode.EditMode: {
        // In Edit mode, GTO is opened only after all current changes were saved.
        if (this.state.project(queryDirty)) {
          this.dialogService.confirmation(yesNoConfirmation('msg_continue_will_save_changes')).pipe(
            filter$(yesAnswer),
            switchMap$(() => {
              const flow = this.completeFlow();

              return flow.run({ trigger: 'process', value: this.form.submitValue, form: this.form })
                .toFinalState().pipe(
                  // we have to guarantee that GTO is opened only after successful save
                  filter$((s) => !!s.success));
            })).subscribe(() => this.showGto());
        } else {
          this.showGto();
        }

        break;
      }
      default:
        // in View mode, we allow to open GTO only if there is at least one measured ToP
        if (this.state.project(queryHpeHasTopsFromGto)) {
          this.showGto();
        } else {
          this.notificationService.warning('msg_no_gto_measurements');
        }
        break;
    }
  }

  private showGto(): void {
    const { state } = this;
    const masterInfo = state.project(queryHpeMasterInfo);
    const allTops = state.project(queryHpeTops);

    this.dialogService.openWithInput(
      graphicalTakeOffMainDialogRoute,
      <GraphicalTakeOffMainConfig>{
        id: state.project(queryHpeHousePlanId),
        entryPoint: 'HP',
        title: `${masterInfo.code} ${masterInfo.name}`,
        allTops,
        gFolderId: masterInfo.masterZoneHouseEstimatePlansFolderId,
      },
      {
        panelClass: 'cdk-overlay-large',
      }).afterClosed().pipe(
      switchMap$(() => this.business.initWithCostCenter(
        {
          housePlanId: state.project(queryHpeHousePlanId),
          priceBookId: this.state.project(queryHpePriceBookId),
          initType: HpeInitType.Open,
        },
        state.project(queryIsCccSelected) ? void 0 : state.project(queryHpeSelectedCostCenterId)).pipe(
        this.waitingService.operator())))
      .subscribe(
        (response) => {
          this.state.mutate({
            type: HpeDetailsStateEventType.LoadWithCostCenter,
            details: response,
            costCenterId: response.initialCostCenterId,
            costCenter: response.initialCostCenter,
          });

          this.updateFormulaContext();
        },
        this.notificationService.processAll,
      );
  }

  private validate(): boolean {
    if (this.state.project(queryHpeHasErrors)) {
      this.state.mutate({ type: HpeDetailsStateEventType.NavigateToInvalid });

      this.notificationService.error('msg_he_incorrect_data_in_tables');
      return false;
    }
    return true;
  }

  private validateCurrentElement(): boolean {
    if (this.state.project(queryHpeElementHasErrors)) {
      this.state.mutate({ type: HpeDetailsStateEventType.HighlightInvalid });
      return false;
    }
    return true;
  }

  private propagateFormulaItemFlow(item: HpeItem): Promise<any> {
    const { formulaContext } = getRouteInitData(this.route);
    return this.formulaPropagation.propagateFormula({
      formulaContext,
      housePlanId: this.state.project(queryHpeHousePlanId),
      costCenterId: item.costCenterId,
      elementId: item.itemId,
      elementCode: item.code,
      isComponent: false,
      newFormula: item.formula,
      oldFormula: item.oldFormula,
    });
  }

  private propagateFormulaComponentFlow(component: HpeComponent): Promise<void> {
    const { formulaContext } = getRouteInitData(this.route);
    return this.formulaPropagation.propagateFormula({
      formulaContext,
      housePlanId: this.state.project(queryHpeHousePlanId),
      elementId: component.componentId,
      elementCode: component.code,
      isComponent: true,
      newFormula: component.formula,
      oldFormula: component.oldFormula,
    });
  }

  private ensureCcLoad(costCenterId?: ID): Observable<HpeDetailsStateEvent> {
    return of$(costCenterId).pipe(
      filter$(Boolean),
      map$((id) => this.state.project(queryHpeCostCenter, id)),
      filter$(Boolean),
      filter$(isHpeCcNotPreloaded),
      switchMap$((cc) =>
        this.business.loadCostCenter(
          this.state.project(queryHpeGetCostCenterRequest, cc.costCenterId),
          this.createHpeMappingContext()).pipe(
          (this.waitingService.operator<HpeDetailsGetCostCenter>()),
          switchMap$((complete) => {
            if (complete.version! > cc.version
              && this.state.project(queryHpeCostCenterHasDirtyChanges, getHpeCostCenterId(cc))) {
              return throwError$(MessageBasket.error('msg_optimistic_lock_exception'));
            } else {
              return [complete];
            }
          }))),
      map$((costCenter) => (<HpeDetailsStateCostCenterLoadEvent>{
        type: HpeDetailsStateEventType.CostCenterLoad,
        costCenterId,
        costCenter,
      })));
  }

  private propagateFormulaFlow(): Observable<HpeDetailsStateEvent> {
    return this.state.select(queryHpeSelectedElementInfo).pipe(
      first$(),
      filter$(Boolean),
      switchMap$(({ type: sourceType, id: sourceId }) =>
        this.state.select(queryHpeElement, sourceType, sourceId).pipe(
          first$(),
          filter$((element) => element.isFormulaNeedPropagation),
          filter$((element) => !this.isFormulaNotPropagatable(element.formula)),
          switchMap$((element) => {
            if (isHpeComponent(element)) {
              return this.propagateFormulaComponentFlow(element);
            } else {
              return this.propagateFormulaItemFlow(element);
            }
          }),
          map$(() => EMPTY$),
          switchAll$(), // makes no value when observable emits something
          concatWith$(of$(<HpeDetailsStateEvent>{
            type: HpeDetailsStateEventType.ResetFormulaNeedPropagation,
            sourceType,
            sourceId,
          })))));
  }

  private createHpeMappingContext(): HpeMappingContext {
    return {
      getUom: (id: ID) => this.state.project(queryHpeUomRefItem, id),
      getTop: (id: ID) => this.state.project(queryHpeTopRefItem, id),
    };
  }

  private testElementFormula(event: FormulaTestEvent, element: HpeElement): void {
    if (element && element.formula) {
      const invalidTop = this.state.project(queryHpeFormulaFirstInvalidTop, element.formula);
      if (invalidTop) {
        this.state.mutate({ type: HpeDetailsStateEventType.NavigateToInvalidTop, topId: getHpeTopId(invalidTop) });
        event.setError(this.translations.get('lbl_incorrect_top_value'));
      } else {
        const request = this.state.project(queryHpeElementFormulaCalculation, element);
        if (request) {
          event.setValue(this.formulaCalculation.calculate(request, NumberTypes.SignedQuantity));
        }
      }
    }
  }

  private updateFormulaContext(): void {
    const { formulaContext } = getRouteInitData(this.route);

    const variables = this.state.project(queryHpeTopRefItems).map(hpeFormulaVariableFromTop);
    const activeVariables = variables.filter(isFormulaVariableActive);
    formulaContext.setVariables(keyBy(variables, 'id'));
    formulaContext.setSnippets(FormulaElementGroup.TakeOffParams, variablesToSnippets(activeVariables));
  }

  private isFormulaNotPropagatable(formula?: Formula): boolean {
    if (isFormulaEmpty(formula)) {
      return true;
    }
    if (!isFormulaValid(formula!)) {
      return true;
    }
    if (this.state.project(queryHpeFormulaHasTopsFromGto, formula!)) {      
      return true;
    }
    return false;
  }

  private ensureDependenciesExist(items: ComponentItemFinderListItem[]): Observable<void> {
    return combineLatest$(this.ensureUomExist(items), this.ensureTopExist(items)).pipe(map$(() => void 0));
  }

  private ensureUomExist(items: ComponentItemFinderListItem[]): Observable<void> {
    const notExistingUomIds = uniq(items
      .map(({ uomId }) => uomId)
      .filter((uomId) => isNil(this.state.project(queryHpeUomRefItem, uomId))));
    if (notExistingUomIds.length > 0) {
      return combineLatest$(notExistingUomIds.map((uomId) => this.business.getUom(uomId))).pipe(
        tap$((uomSet) => this.state.mutate({ type: HpeDetailsStateEventType.UomAddSet, uomSet })),
        map$(() => void 0));
    } else {
      return of$(void 0);
    }
  }

  private ensureTopExist(items: ComponentItemFinderListItem[]): Observable<void> {
    const notExistingTopIds = uniq(flatMap(items, ({ topIds }) => topIds || []))
      .filter((topId) => isNil(this.state.project(queryHpeTopRefItem, topId)));
    if (notExistingTopIds.length > 0) {
      return combineLatest$(notExistingTopIds.map((topId) => this.business.getTop(topId))).pipe(
        tap$((topSet) => {
          this.state.mutate({ type: HpeDetailsStateEventType.TopAddSet, topSet });
          this.updateFormulaContext();
        }),
        map$(() => void 0));
    } else {
      return of$(void 0);
    }
  }
}
